import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import PriceListPage from './pages/PriceListPage';
import HomePage from './pages/HomePage';
import OnlineShopPage from './pages/OnlineShopPage';
import ProtectedRoute from './routes/ProtectedRoute';
import RequirePermission from './routes/RequirePermission';
import RequireBranchAccess from './routes/RequireBranchAccess';
import PurchasesListPage from './pages/PurchasesListPage';
import EditPurchasePage from './pages/EditPurchasePage';
import PurchaseDetailsPage from './pages/PurchaseDetailsPage';
import ClientsListPage from './pages/ClientsListPage';
import ClientPurchasesPage from './pages/ClientPurchasesPage';
import ClientRegistrationPage from './pages/ClientRegistrationPage';
import ClientPortalLoginPage from './pages/ClientPortalLoginPage';
import ClientPortalPasswordPage from './pages/ClientPortalPasswordPage';
import ClientQuotationRequestPage from './pages/ClientQuotationRequestPage';
import ClientPortalPurchaseOrdersPage from './pages/ClientPortalPurchaseOrdersPage';
import ClientPurchaseOrderEditorPage from './pages/ClientPurchaseOrderEditorPage';
import EditClientPage from './pages/EditClientPage';
import MyProfilePage from './pages/MyProfilePage';
import StockCreatePage from './pages/StockCreatePage';
import StockListsView from './pages/StockListsView';
import StockListViewPage from './pages/StockListViewPage';
import StockExpenseCreationPage from './pages/StockExpenseCreationPage';
import InventoryPage from './pages/InventoryPage';
import StockRequestsListPage from './pages/StockRequestsListPage';
import StockRequestDetailPage from './pages/StockRequestDetailPage';
import PurchaseReceiptPage from './pages/PurchaseReceiptPage';
import PurchaseQuotePage from './pages/PurchaseQuotePage';
import QuotationsListPage from './pages/QuotationsListPage';
import QuotationDetailPage from './pages/QuotationDetailPage';
import QuotationBuilderPage from './pages/QuotationBuilderPage';
import PurchaseOrdersListPage from './pages/PurchaseOrdersListPage';
import PurchaseOrderDetailPage from './pages/PurchaseOrderDetailPage';
import BranchListPage from './pages/BranchListPage';
import BranchExpensesPage from './pages/BranchExpensesPage';
import CashRegisterLogsPage from './pages/CashRegisterLogsPage';
import DeliveryGuidesListPage from './pages/DeliveryGuidesListPage';
import DeliveryGuideCreatePage from './pages/DeliveryGuideCreatePage';
import DeliveryGuideEditPage from './pages/DeliveryGuideEditPage';
import DeliveryGuideDetailsPage from './pages/DeliveryGuideDetailsPage';
import ProvidersListPage from './pages/ProvidersListPage';
import ProviderFormPage from './pages/ProviderFormPage';
import ProviderDetailPage from './pages/ProviderDetailPage';
import AdminPriceListPage from './pages/AdminPriceListPage';
import ReportsPage from './pages/ReportsPage';
import ReportsFinancePage from './pages/ReportsFinancePage';
import ReportsReceivablesPage from './pages/ReportsReceivablesPage';
import ReportsSalesPage from './pages/ReportsSalesPage';
import SettingsPage from './pages/SettingsPage';
import Navbar from './components/Navbar';
import AppAlerts from './components/AppAlerts';
import PurchaseEventsProvider from './components/PurchaseEventsProvider';
import { ConfirmDialogProvider } from './hooks/useConfirmDialog';
import './styles/index.css';

const withProtected = (element, permissions = []) => (
  <ProtectedRoute>
    <RequirePermission permissions={permissions}>
      {element}
    </RequirePermission>
  </ProtectedRoute>
);

const withBranch = (element, permissions = []) => (
  <ProtectedRoute>
    <RequirePermission permissions={permissions}>
      <RequireBranchAccess>
        {element}
      </RequireBranchAccess>
    </RequirePermission>
  </ProtectedRoute>
);

const App = () => (
  <ConfirmDialogProvider>
    <Router>
      <Navbar />
      <AppAlerts />
      <PurchaseEventsProvider />
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/client-portal" element={<Navigate to="/client-portal/request" replace />} />
        <Route path="/client-portal/login" element={<ClientPortalLoginPage />} />
        <Route path="/client-portal/change-password" element={<ClientPortalPasswordPage />} />
        <Route path="/client-portal/request" element={<ClientQuotationRequestPage mode="quotation" />} />
        <Route path="/client-portal/purchase-orders/new" element={<ClientPurchaseOrderEditorPage />} />
        <Route path="/client-portal/purchase-orders/:id/edit" element={<ClientPurchaseOrderEditorPage />} />
        <Route path="/client-portal/purchase-orders" element={<ClientPortalPurchaseOrdersPage />} />
        <Route path="/branches/:branchId/catalog" element={<PriceListPage />} />
        <Route path="/home" element={withProtected(<HomePage />)} />
        <Route path="/branches" element={
          withProtected(<BranchListPage />, ['branch.read'])
        } />
        <Route path="/settings" element={
          withProtected(<SettingsPage />)
        } />
        <Route path="/branches/:branchId/users" element={
          withProtected(
            <Navigate to="/settings?topic=users&section=users-access" replace />,
            ['branch.access.any']
          )
        } />
        <Route path="/branches/:branchId/cash-registers" element={
          withProtected(
            <Navigate to="/settings?topic=registers&section=registers-cash" replace />,
            ['branch.access.any']
          )
        } />
        <Route path="/branches/:branchId/cash-register-logs" element={
          withBranch(<CashRegisterLogsPage />, ['cash_register.logs.read'])
        } />
        <Route path="/branches/:branchId/expenses" element={
          withBranch(<BranchExpensesPage />, ['branch_expense.read'])
        } />
        <Route path="/branches/:branchId/settings" element={
          withProtected(
            <Navigate to="/settings?topic=registers&section=registers-barcode" replace />,
            ['branch.access.any']
          )
        } />
        <Route path="/branches/:branchId/purchases" element={
          withBranch(<PurchasesListPage />, ['sales.read'])
        } />
        <Route path="/branches/:branchId/purchases/:id/edit" element={
          withBranch(<EditPurchasePage />, ['sales.update'])
        } />
        <Route path="/branches/:branchId/purchases/:id" element={
          withBranch(<PurchaseDetailsPage />, ['sales.read'])
        } />
        <Route path="/branches/:branchId/purchases/new" element={
          withBranch(< OnlineShopPage />, ['sales.create'])
        } />
        <Route path="/branches/:branchId/price-list" element={
          withBranch(<AdminPriceListPage />, ['product.read'])
        } />
        <Route path="/branches/:branchId/delivery-guides" element={
          withBranch(<DeliveryGuidesListPage />, ['delivery_guide.read'])
        } />
        <Route path="/branches/:branchId/purchases/:purchaseId/receipt" element={
          withBranch(<PurchaseReceiptPage />, ['sales.read'])
        } />
        <Route path="/branches/:branchId/purchases/:purchaseId/receipt/preview" element={
          withBranch(<PurchaseReceiptPage previewOnly />, ['sales.read'])
        } />
        <Route path="/branches/:branchId/purchases/:purchaseId/quote" element={
          withBranch(<PurchaseQuotePage />, ['sales.read'])
        } />
        <Route path="/branches/:branchId/quotations" element={
          withBranch(<QuotationsListPage />, ['quotation.read'])
        } />
        <Route path="/branches/:branchId/quotations/new" element={
          withBranch(<QuotationBuilderPage />, ['quotation.create'])
        } />
        <Route path="/branches/:branchId/quotations/:id" element={
          withBranch(<QuotationDetailPage />, ['quotation.read'])
        } />
        <Route path="/branches/:branchId/quotations/:id/edit" element={
          withBranch(<QuotationBuilderPage />, ['quotation.update'])
        } />
        <Route path="/branches/:branchId/purchase-orders" element={
          withBranch(<PurchaseOrdersListPage />, ['purchase_order.read'])
        } />
        <Route path="/branches/:branchId/purchase-orders/:id" element={
          withBranch(<PurchaseOrderDetailPage />, ['purchase_order.read'])
        } />
        <Route path="/branches/:branchId/purchases/:purchaseId/delivery-guides/new" element={
          withBranch(<DeliveryGuideCreatePage />, ['delivery_guide.create'])
        } />
        <Route path="/branches/:branchId/purchases/:purchaseId/delivery-guides/:guideId" element={
          withBranch(<DeliveryGuideEditPage />, ['delivery_guide.update'])
        } />
        <Route path="/branches/:branchId/purchases/:purchaseId/delivery-guides/:guideId/details" element={
          withBranch(<DeliveryGuideDetailsPage />, ['delivery_guide.read'])
        } />
        <Route path="/branches/:branchId/delivery-guides/:guideId/details" element={
          withBranch(<DeliveryGuideDetailsPage />, ['delivery_guide.read'])
        } />
        <Route path="/branches/:branchId/reports" element={
          withBranch(
            <ReportsPage />,
            ['stock.read', 'branch_expense.read', 'client.read']
          )
        } />
        <Route path="/branches/:branchId/reports/finances" element={
          withBranch(
            <ReportsFinancePage />,
            ['sales.read', 'stock.read', 'branch_expense.read', 'client.read']
          )
        } />
        <Route path="/branches/:branchId/reports/sales" element={
          withBranch(
            <ReportsSalesPage />,
            ['sales.read', 'branch_expense.read']
          )
        } />
        <Route path="/branches/:branchId/reports/receivables" element={
          withBranch(
            <ReportsReceivablesPage />,
            ['client.read']
          )
        } />
        <Route path="/branches/:branchId/stock" element={
          withBranch(<StockListsView />, ['stock.read'])
        } />
        <Route path="/branches/:branchId/inventory" element={
          withBranch(<InventoryPage />, ['stock.read'])
        } />
        <Route path="/branches/:branchId/stock-requests" element={
          withBranch(<StockRequestsListPage />, ['stock.read'])
        } />
        <Route path="/branches/:branchId/stock-requests/:id" element={
          withBranch(<StockRequestDetailPage />, ['stock.read'])
        } />
        <Route path="/branches/:branchId/providers" element={
          withBranch(<ProvidersListPage />, ['provider.read'])
        } />
        <Route path="/branches/:branchId/providers/new" element={
          withBranch(<ProviderFormPage />, ['provider.create'])
        } />
        <Route path="/branches/:branchId/providers/:id/edit" element={
          withBranch(<ProviderFormPage />, ['provider.update'])
        } />
        <Route path="/branches/:branchId/providers/:id" element={
          withBranch(<ProviderDetailPage />, ['provider.read'])
        } />
        <Route path="/branches/:branchId/stock/:id" element={
          withBranch(<StockListViewPage />, ['stock.read'])
        } />
        <Route path="/branches/:branchId/stock/:id/create" element={
          withBranch(<StockCreatePage />, ['stock.create'])
        } />
        <Route path="/branches/:branchId/stock/:id/create-expense" element={
          withBranch(<StockExpenseCreationPage />, ['stock.create'])
        } />
        <Route path="/change-password" element={
          withProtected(<MyProfilePage />, ['profile.read'])
        } />
        <Route path="/client" element={
          withProtected(<ClientsListPage />, ['client.read'])
        } />
        <Route path="/client/:id" element={
          withProtected(<ClientPurchasesPage />, ['client.read'])
        } />
        <Route path="/client/:id/edit" element={
          withProtected(<EditClientPage />, ['client.update'])
        } />
        <Route path="/client/new" element={
          withProtected(<ClientRegistrationPage />, ['client.create'])
        } />
      </Routes>
    </Router>
  </ConfirmDialogProvider>
);

export default App;
